/**
 * @file security.js
 * @description Zentrale Sicherheits- und Validierungskonfigurationen für ThekenOS.
 *
 * Diese Datei bündelt alle systemweiten Sicherheitsregeln an einem zentralen Ort:
 * 1. Benutzernamen-Formate (Länge und erlaubte Zeichen)
 * 2. Bedienbremse beim Login (siehe Warnhinweis bei LOGIN_RATE_LIMIT)
 * 3. Datei-Upload-Beschränkungen (erlaubte Dateitypen und Maximalgrößen)
 * 4. Path-Traversal-Schutz für Dateinamen
 * 5. CAPTCHA-Integration (z. B. Cloudflare Turnstile zur Bot-Abwehr)
 * 6. Berechtigungsregeln, die Navigation und Routen-Wächter gemeinsam nutzen
 *
 * GRUNDSATZ: Alles in dieser Datei läuft im Browser und ist damit für den Benutzer
 * veränderbar. Es sind Komfort- und Führungsregeln, keine Zugriffskontrolle. Verbindlich
 * sind ausschließlich die Row-Level-Security-Policies und die Edge Functions auf dem Server.
 */

/**
 * Validierungsregeln für Benutzernamen:
 * - minLength: Mindestens 3 Zeichen lang
 * - maxLength: Maximal 32 Zeichen lang
 * - pattern: Muss mit einem Kleinbuchstaben oder einer Ziffer beginnen;
 *   erlaubt sind Kleinbuchstaben (a-z), Ziffern (0-9), Punkte (.), Unterstriche (_) und Bindestriche (-)
 */
export const USERNAME_RULES = {
  minLength: 3,
  maxLength: 32,
  pattern: /^[a-z0-9][a-z0-9._-]*$/,
};

/**
 * Konfiguration der lokalen Login-Bremse.
 *
 * ACHTUNG – das ist KEIN Brute-Force-Schutz:
 * Der Fehlversuchszähler liegt im LocalStorage des Browsers (Schlüssel
 * `thekeos_login_state_v1`) und ist mit `localStorage.clear()` oder einem privaten Fenster
 * in Sekunden zurückgesetzt. Die Sperre existiert ausschließlich, um einer echten Person nach
 * mehreren Vertippern eine Pause zu signalisieren.
 *
 * Der wirksame Brute-Force-Schutz sitzt in der Edge Function `auth-login` und meldet sich mit
 * HTTP 429 plus `Retry-After`-Header; ausgewertet in `edgeFunction.js` und `LoginPage.jsx`.
 * Änderungen an den Werten hier verbessern die Sicherheit NICHT.
 *
 * - maxAttempts: Nach 5 Fehlversuchen wird lokal gesperrt
 * - windowMs: Zeitfenster von 15 Minuten zur Zählung der Fehlversuche
 * - lockMs: Lokale Sperrzeit von 5 Minuten nach Überschreiten des Limits
 */
export const LOGIN_RATE_LIMIT = {
  maxAttempts: 5,
  windowMs: 15 * 60 * 1000,
  lockMs: 5 * 60 * 1000,
};

/**
 * Upload-Regeln für ThekenMail-Dokumente:
 * - allowedMimeTypes: Aus Sicherheits- und Kompatibilitätsgründen sind ausschließlich PDF-Dateien zulässig.
 * - maxFileSizeBytes: Maximal 10 Megabyte Dateigröße je Dokument.
 */
export const MAIL_UPLOAD_RULES = {
  allowedMimeTypes: ['application/pdf'],
  maxFileSizeBytes: 10 * 1024 * 1024,
};

/**
 * Upload-Regeln für Bilder im Thekipedia-Wiki:
 * - allowedMimeTypes: Gängige Web-Bildformate (JPEG, PNG, WebP, GIF)
 * - maxFileSizeBytes: Maximal 5 Megabyte je Bilddatei zur Schonung von Speicher und Ladezeiten.
 */
export const ARTICLE_IMAGE_UPLOAD_RULES = {
  allowedMimeTypes: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'],
  maxFileSizeBytes: 5 * 1024 * 1024,
};

/**
 * Validierungsmuster für Dateinamen/Pfade bei ThekenMails.
 * Verhindert sogenannte "Path-Traversal"-Angriffe (z. B. "../../../etc/passwd"),
 * indem nur sichere Zeichen ohne relative Pfadnavigation erlaubt werden.
 */
export const MAIL_PATH_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;

/**
 * Konfiguration für Bot-Schutz / CAPTCHA (z. B. Cloudflare Turnstile).
 * Verwendet primär Umgebungsvariablen (`VITE_CAPTCHA_PROVIDER`, `VITE_TURNSTILE_SITE_KEY`),
 * besitzt jedoch sichere Produktions-Defaults, damit der Bot-Schutz im Live-Betrieb
 * (z. B. auf Cloudflare Pages) auch ohne explizite .env-Konfiguration immer aktiv ist.
 */
export const CAPTCHA = {
  provider: (import.meta.env.VITE_CAPTCHA_PROVIDER || 'turnstile').toLowerCase(),
  turnstileSiteKey: import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAEYVr2FIiCthBQN-',
};

/**
 * Rollen, die den Einkaufsbereich ohne gesonderte Zusatzberechtigung sehen dürfen.
 * Redakteure benötigen zusätzlich das Einkaufsrecht (`permissions.hasEinkauf`).
 */
const EINKAUF_BASE_ROLES = ['Admin', 'System-Admin', 'Einkäufer'];

/**
 * Einzige Wahrheitsquelle für den Zugang zum Einkaufsbereich (`/einkauf`).
 *
 * Diese Funktion MUSS von der Navigation (Sidebar) und vom Routen-Wächter gleichermaßen
 * verwendet werden. Werden beide Stellen getrennt gepflegt, laufen sie auseinander: Zuvor
 * blendete die Sidebar den Punkt für Redakteure ohne Einkaufsrecht aus, während die Route
 * jeden Redakteur durchließ – das Recht `has_einkauf` war damit wirkungslos, sobald jemand
 * die Adresse direkt eingab.
 *
 * Hinweis: Der verbindliche Schutz sind die RLS-Policies auf `shopping_items` und
 * `shopping_stores`. Diese Prüfung steuert nur, was die Oberfläche anbietet.
 *
 * @param {string|null} role - Die Systemrolle des Benutzers
 * @param {{ hasEinkauf?: boolean }} [permissions] - Zusatzberechtigungen aus dem AuthContext
 * @returns {boolean} true, wenn der Einkaufsbereich angezeigt und betreten werden darf
 */
export function canAccessEinkauf(role, permissions) {
  if (EINKAUF_BASE_ROLES.includes(role)) return true;
  return role === 'Redakteur' && permissions?.hasEinkauf === true;
}


